import type { FeatherParticle } from './huntParticles';
import { drawFeathers, spawnFeathers, updateFeathers } from './huntParticles';
import { HUNT_BIRD_MOTION_FX, HUNT_SPRITE_SHEETS } from './huntConfig';
import { HuntBird, HuntGameSnapshot } from './huntEngine';
import {
    drawSpriteFrame,
    getBirdFlyFrame,
    getBirdSheetKey,
} from './huntSprites';

export interface HuntBackgroundImages {
    sky?: HTMLImageElement;
}

export interface HuntUiImages {
    crosshair?: HTMLImageElement;
}

function drawAnimatedClouds(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    timeMs: number
): void {
    const drift = (timeMs * 0.012) % (width + 240);
    const clouds = [
        { bx: width * 0.12, by: height * 0.14, w: 92, h: 28 },
        { bx: width * 0.48, by: height * 0.08, w: 110, h: 32 },
        { bx: width * 0.78, by: height * 0.18, w: 78, h: 24 },
    ];

    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.42)';
    clouds.forEach(({ bx, by, w, h }, index) => {
        const x = ((bx + drift * (0.35 + index * 0.08)) % (width + w)) - w * 0.5;
        ctx.beginPath();
        ctx.ellipse(x, by, w * 0.42, h * 0.45, 0, 0, Math.PI * 2);
        ctx.ellipse(x + w * 0.28, by - h * 0.08, w * 0.34, h * 0.38, 0, 0, Math.PI * 2);
        ctx.ellipse(x - w * 0.22, by + h * 0.04, w * 0.28, h * 0.32, 0, 0, Math.PI * 2);
        ctx.fill();
    });
    ctx.restore();
}

export function drawHuntBackground(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    timeMs: number,
    images?: HuntBackgroundImages,
    reducedMotion = false
): void {
    if (images?.sky?.complete && images.sky.naturalWidth > 0) {
        ctx.drawImage(images.sky, 0, 0, width, height);
    } else {
        drawProceduralSky(ctx, width, height, timeMs, reducedMotion);
    }

    if (!reducedMotion) {
        drawAnimatedClouds(ctx, width, height, timeMs);
    }
}

function drawProceduralSky(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    timeMs: number,
    reducedMotion: boolean
): void {
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, '#4a9fd4');
    gradient.addColorStop(0.55, '#a8d8f0');
    gradient.addColorStop(0.82, '#eef6fb');
    gradient.addColorStop(1, '#d5dee6');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    const sunY = height * 0.16 + (reducedMotion ? 0 : Math.sin(timeMs / 4000) * 3);
    const sunGrad = ctx.createRadialGradient(width * 0.84, sunY, 4, width * 0.84, sunY, 48);
    sunGrad.addColorStop(0, 'rgba(255, 248, 210, 0.9)');
    sunGrad.addColorStop(1, 'rgba(255, 248, 210, 0)');
    ctx.fillStyle = sunGrad;
    ctx.beginPath();
    ctx.arc(width * 0.84, sunY, 48, 0, Math.PI * 2);
    ctx.fill();
}

export function drawBirdSprite(
    ctx: CanvasRenderingContext2D,
    bird: HuntBird,
    images: Record<string, HTMLImageElement | undefined>,
    now: number,
    reducedMotion: boolean
): void {
    const sheetKey = getBirdSheetKey(bird.type, bird.state);
    const img = images[sheetKey];
    const sheetDef = HUNT_SPRITE_SHEETS[sheetKey as keyof typeof HUNT_SPRITE_SHEETS];
    if (!img || !sheetDef) {
        return;
    }

    const bob = bird.state === 'fly' && HUNT_BIRD_MOTION_FX && !reducedMotion
        ? Math.sin((now / 100) + bird.id) * 5
        : 0;
    const flapScale = bird.state === 'fly' && HUNT_BIRD_MOTION_FX && !reducedMotion
        ? 1 + Math.sin((now / 80) + bird.frame * 2) * 0.06
        : 1;

    ctx.save();
    ctx.translate(bird.x + bird.width / 2, bird.y + bird.height / 2 + bob);
    ctx.scale(bird.direction * flapScale, flapScale);
    ctx.rotate(bird.rotation);

    const frame = bird.state === 'fall' ? 0 : getBirdFlyFrame(bird, reducedMotion);
    drawSpriteFrame(
        ctx,
        img,
        sheetDef,
        frame,
        -bird.width / 2,
        -bird.height / 2,
        bird.width,
        bird.height
    );
    ctx.restore();
}

export function drawCrosshair(
    ctx: CanvasRenderingContext2D,
    snapshot: HuntGameSnapshot,
    crosshair?: HTMLImageElement
): void {
    if (snapshot.pointerX < 0) {
        return;
    }
    const { pointerX: x, pointerY: y } = snapshot;
    const size = 48;

    if (crosshair?.complete && crosshair.naturalWidth > 0) {
        ctx.drawImage(crosshair, x - size / 2, y - size / 2, size, size);
        return;
    }

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.95)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(x, y, 22, 0, Math.PI * 2);
    ctx.moveTo(x - 28, y);
    ctx.lineTo(x - 8, y);
    ctx.moveTo(x + 8, y);
    ctx.lineTo(x + 28, y);
    ctx.moveTo(x, y - 28);
    ctx.lineTo(x, y - 8);
    ctx.moveTo(x, y + 8);
    ctx.lineTo(x, y + 28);
    ctx.stroke();

    ctx.fillStyle = 'rgba(2, 48, 71, 0.85)';
    ctx.beginPath();
    ctx.arc(x, y, 4, 0, Math.PI * 2);
    ctx.fill();
}

export { spawnFeathers, updateFeathers, drawFeathers };
export type { FeatherParticle };
